import React from 'react'

import SupportTabs from '../screens/SupportTabs'
import SupportTicketCreate from '../screens/SupportTicketCreate'
import SupportTicketDetail from '../screens/SupportTicketDetail'
import { allRoles, RouteWithAccessType } from './types'

/**
 * Support ticketing (ADR-017 v0.7, ADR-035 v0.2).
 *
 * ACCESS IS `allRoles` ON PURPOSE. Every panel user can file a ticket about
 * their own work and read the ones they are party to — a support desk that only
 * some roles may reach is a support desk people route around. Narrowing happens
 * per row on the server: ticket-service applies the §2.6.1 visibility predicate
 * to every read, so a caller sees only tickets they report, are assigned, watch,
 * or that affect an entity in their scope. A role list here could only ever
 * remove the whole section — it cannot express per-ticket visibility, and
 * pretending otherwise would put the real access decision in the browser.
 *
 * `requiredFeatures: []` — ticketing is not feature-flagged; it is the channel
 * through which a user reports that some *other* feature is broken.
 *
 * ROUTE SHAPE: detail sits under a literal `/detail/` segment rather than
 * `/support/tickets/:id`, so it cannot be confused with a sibling.
 *
 * NOTE, corrected 2026-08: an earlier version of this comment claimed
 * `panelRoutes.tsx` renders every matching route side-by-side with no
 * `<Switch>`. That has not been true since `1ee76bd6` ("Fix overlapping panel
 * routes") — there IS a `<Switch>`, so only the first match renders and a
 * dual-mount is structurally impossible. The literal-segment shape is still
 * worth keeping (it makes the patterns non-overlapping on their own, which is
 * cheaper to reason about than route ORDER), but it is no longer load-bearing.
 */
/**
 * The two per-screen access guards that used to live here are gone: the admin
 * screens are no longer routed individually, so the gate moved to SupportTabs,
 * which is the one place that now decides both which tabs exist and which
 * panels render. Two copies of "is this user an admin" for the same screen is
 * how the two answers eventually disagree.
 */
const supportRoleManagement: RouteWithAccessType[] = [
  {
    // Create and detail stay separate routes.
    //
    // They are listed first for readability, not because order decides
    // anything: `panelRoutes` wraps the list in a `<Switch>` and `exact` plus a
    // single-segment `:page` already makes these patterns mutually exclusive.
    // `/support/tickets/create` has two segments after `/support`, so the
    // one-segment `/support/:page` cannot match it whatever the order.
    route: '/support/tickets/create',
    exact: true,
    rolesListWithReadAccess: allRoles,
    rolesListWithWriteAccess: allRoles,
    requiredFeatures: [],
    component: <SupportTicketCreate />
  },
  {
    route: '/support/tickets/detail/:id',
    exact: true,
    rolesListWithReadAccess: allRoles,
    rolesListWithWriteAccess: allRoles,
    requiredFeatures: [],
    component: <SupportTicketDetail />
  },
  {
    // Everything else is one tabbed screen behind one drawer entry.
    //
    // READ THIS BEFORE TRUSTING THE ROLE LIST BELOW. `canAccessRoute` and
    // `canAccessByRoutePath` (src/cache/auth.ts) return true immediately when
    // `requiredFeatures` is empty and NEVER read rolesListWithReadAccess — the
    // field is inert at the routing layer across this whole app. It is
    // therefore `allRoles` here to describe what it actually does: admit every
    // role to the section. The ADMIN-only SUB-PAGES are hidden by SupportTabs,
    // which omits their tabs and refuses their segments for a non-admin.
    //
    // Even those are affordances. The data is protected server-side by ADR-035
    // KDD-2's three independent gates: a Layer-1 permission on the operation,
    // the BFF re-checking the verified is_admin claim, and ticket-service
    // re-deriving the admin tier from the JWT rather than trusting the BFF's
    // routing flag.
    route: '/support/:page',
    exact: true,
    rolesListWithReadAccess: allRoles,
    rolesListWithWriteAccess: allRoles,
    requiredFeatures: [],
    // Declared so SupportTabs can filter through canAccessTab like the other 15
    // tabbed screens (portal-ux-conventions §4.3).
    //
    // Read the role lists here as DOCUMENTATION, not enforcement. canAccessTab
    // consults `requiredFeatures` only — never these — so a per-tab role list is
    // inert, exactly as the route-level one is. No *Tabs screen in the portal
    // gates by role at all; SupportTabs keeps an explicit getRoleFromStorage
    // check for the two admin tabs, because dropping it in the name of matching
    // the convention would put a cross-tenant queue back in every tenant's view.
    tabs: [
      { params: { page: 'mine' }, requiredFeatures: [] },
      { params: { page: 'affecting-us' }, requiredFeatures: [] },
      { params: { page: 'assigned' }, requiredFeatures: [] },
      // NO rolesListWithReadAccess on the two admin tabs, deliberately.
      // Nothing reads a tab's role list — canAccessTab consults
      // `requiredFeatures` only — so declaring one here would sit inches from a
      // live-looking field and read as "this tab is gated by role" in a file
      // where the identically-named field IS enforced elsewhere. That is the
      // exact shape of the defect this feature already had once.
      // The gate lives in SupportTabs' SUPPORT_TABS[].adminOnly plus its
      // getRoleFromStorage check. Adding a sixth tab means adding it there.
      { params: { page: 'queue' }, requiredFeatures: [] },
      { params: { page: 'sla-policies' }, requiredFeatures: [] }
    ],
    component: <SupportTabs />
  },
  {
    // Kept so an existing bookmark, a notification deep link, or an old email
    // still lands somewhere. `/support/tickets` was the list before the tabs
    // existed; it now resolves to the default tab rather than 404ing.
    route: '/support',
    exact: true,
    rolesListWithReadAccess: allRoles,
    rolesListWithWriteAccess: allRoles,
    requiredFeatures: [],
    component: <SupportTabs />
  }
]

export default supportRoleManagement
